import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, SlidersHorizontal, Trash2, User, Users, X } from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input, Label, Select } from '@/components/ui/Input';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Drawer } from '@/components/ui/Drawer';
import { Banner } from '@/components/ui/Banner';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { displayName } from '@shared/prospect';
import { configApi, type ContractType } from '@/services/config.api';
import { jeopardyApi, type JeopardyEntry, type JeopardyFilters } from '@/services/jeopardy.api';
import { CreateJeopardyModal } from '@/components/CreateJeopardyModal';

// Clés de filtre présentes dans l'URL. Si AUCUNE n'est là (nav propre depuis
// la sidebar), on applique le défaut « Mon pipe » (comme la page Pipeline).
const FILTER_PARAM_KEYS = [
  'search',
  'contract_number',
  'contract_type_id',
  'min_premium',
  'max_premium',
  'min_commission',
  'max_commission',
  'due_after',
  'due_before',
  'assigned_user_id',
] as const;

export function ClientJeopardyPage() {
  // Le contenu vit dans un sous-composant rendu DANS <AppLayout> : les hooks
  // useConfirm()/useToast() dépendent des providers (ConfirmProvider est monté
  // par AppLayout). Les appeler au niveau du composant qui rend AppLayout les
  // exécute HORS provider → crash. Pattern documenté.
  return (
    <AppLayout>
      <ClientJeopardyContent />
    </AppLayout>
  );
}

function ClientJeopardyContent() {
  const token = useAuthStore((s) => s.accessToken)!;
  const currentUserId = useAuthStore((s) => s.user?.id) ?? '';
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const isDeepLink = useMemo(
    () => FILTER_PARAM_KEYS.some((k) => searchParams.has(k)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // --- Filtres (état local, synchronisé avec l'URL) ---
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [contractNumber, setContractNumber] = useState(searchParams.get('contract_number') ?? '');
  const [contractTypeId, setContractTypeId] = useState(searchParams.get('contract_type_id') ?? '');
  const [minPremium, setMinPremium] = useState(searchParams.get('min_premium') ?? '');
  const [maxPremium, setMaxPremium] = useState(searchParams.get('max_premium') ?? '');
  const [minCommission, setMinCommission] = useState(searchParams.get('min_commission') ?? '');
  const [maxCommission, setMaxCommission] = useState(searchParams.get('max_commission') ?? '');
  const [dueAfter, setDueAfter] = useState(searchParams.get('due_after') ?? '');
  const [dueBefore, setDueBefore] = useState(searchParams.get('due_before') ?? '');
  // « Mon » (assigned_user_id = moi) vs « Équipe » (vide, borné par le scope).
  const [assignedUserId, setAssignedUserId] = useState(
    searchParams.get('assigned_user_id') ?? (isDeepLink ? '' : currentUserId),
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);

  const [items, setItems] = useState<JeopardyEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<JeopardyEntry | null>(null);

  // Référentiel type de contrat (pour le filtre + les libellés des pills).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cts = await configApi.listContractTypes(token);
        if (!cancelled) setContractTypes(cts.filter((c) => c.isActive));
      } catch {
        /* le filtre reste vide, non bloquant */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const apiFilters = useMemo<JeopardyFilters>(() => {
    const f: JeopardyFilters = { sort: 'due_date_asc', limit: 50 };
    if (search.trim()) f.search = search.trim();
    if (contractNumber.trim()) f.contract_number = contractNumber.trim();
    if (contractTypeId) f.contract_type_id = contractTypeId;
    if (minPremium !== '') f.min_premium = Number(minPremium);
    if (maxPremium !== '') f.max_premium = Number(maxPremium);
    if (minCommission !== '') f.min_commission = Number(minCommission);
    if (maxCommission !== '') f.max_commission = Number(maxCommission);
    if (dueAfter) f.due_after = dueAfter;
    if (dueBefore) f.due_before = dueBefore;
    if (assignedUserId) f.assigned_user_id = assignedUserId;
    return f;
  }, [
    search,
    contractNumber,
    contractTypeId,
    minPremium,
    maxPremium,
    minCommission,
    maxCommission,
    dueAfter,
    dueBefore,
    assignedUserId,
  ]);

  // Sync URL (remplacement, pas d'entrée d'historique par frappe).
  useEffect(() => {
    const next = new URLSearchParams();
    if (search.trim()) next.set('search', search.trim());
    if (contractNumber.trim()) next.set('contract_number', contractNumber.trim());
    if (contractTypeId) next.set('contract_type_id', contractTypeId);
    if (minPremium !== '') next.set('min_premium', minPremium);
    if (maxPremium !== '') next.set('max_premium', maxPremium);
    if (minCommission !== '') next.set('min_commission', minCommission);
    if (maxCommission !== '') next.set('max_commission', maxCommission);
    if (dueAfter) next.set('due_after', dueAfter);
    if (dueBefore) next.set('due_before', dueBefore);
    if (assignedUserId) next.set('assigned_user_id', assignedUserId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    contractNumber,
    contractTypeId,
    minPremium,
    maxPremium,
    minCommission,
    maxCommission,
    dueAfter,
    dueBefore,
    assignedUserId,
  ]);

  const load = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await jeopardyApi.list(token, { ...apiFilters, cursor });
        setItems((prev) => (cursor ? [...prev, ...res.data] : res.data));
        setNextCursor(res.hasMore ? res.cursor : null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    },
    [token, apiFilters],
  );

  // Recharge à chaque changement de filtre (page 1).
  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete(entry: JeopardyEntry) {
    const ok = await confirm({
      title: 'Retirer ce contrat de la liste ?',
      message: `Le signalement du contrat « ${entry.contractNumber} » pour ${displayName(
        entry.prospect,
      )} sera supprimé.`,
      variant: 'danger',
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
    });
    if (!ok) return;
    try {
      await jeopardyApi.remove(token, entry.id);
      toast({
        tone: 'success',
        title: 'Signalement supprimé',
        message: 'Le contrat a été retiré de la liste.',
      });
      void load();
    } catch (err) {
      toast({
        tone: 'danger',
        title: 'Suppression impossible',
        message: err instanceof Error ? err.message : 'Erreur',
      });
    }
  }

  const activeFilters = useMemo(() => {
    const list: Array<{ key: string; label: string }> = [];
    if (search.trim()) list.push({ key: 'search', label: `Client : ${search.trim()}` });
    if (contractNumber.trim())
      list.push({ key: 'contract_number', label: `N° : ${contractNumber.trim()}` });
    if (contractTypeId) {
      const ct = contractTypes.find((c) => c.id === contractTypeId);
      list.push({ key: 'contract_type_id', label: `Type : ${ct ? ct.code : '…'}` });
    }
    if (minPremium !== '' || maxPremium !== '')
      list.push({ key: 'premium', label: `Prime : ${rangeLabel(minPremium, maxPremium)}` });
    if (minCommission !== '' || maxCommission !== '')
      list.push({ key: 'commission', label: `Com. : ${rangeLabel(minCommission, maxCommission)}` });
    if (dueAfter || dueBefore)
      list.push({ key: 'due', label: `Échéance : ${dateRangeLabel(dueAfter, dueBefore)}` });
    return list;
  }, [
    search,
    contractNumber,
    contractTypeId,
    contractTypes,
    minPremium,
    maxPremium,
    minCommission,
    maxCommission,
    dueAfter,
    dueBefore,
  ]);

  function clearFilter(key: string) {
    switch (key) {
      case 'search':
        setSearch('');
        break;
      case 'contract_number':
        setContractNumber('');
        break;
      case 'contract_type_id':
        setContractTypeId('');
        break;
      case 'premium':
        setMinPremium('');
        setMaxPremium('');
        break;
      case 'commission':
        setMinCommission('');
        setMaxCommission('');
        break;
      case 'due':
        setDueAfter('');
        setDueBefore('');
        break;
    }
  }

  function clearAllFilters() {
    setSearch('');
    setContractNumber('');
    setContractTypeId('');
    setMinPremium('');
    setMaxPremium('');
    setMinCommission('');
    setMaxCommission('');
    setDueAfter('');
    setDueBefore('');
  }

  const columns: Column<JeopardyEntry>[] = [
    {
      key: 'prospect',
      header: 'Client',
      render: (e) => (
        <div
          className="max-w-[240px] truncate font-medium text-brand"
          title={displayName(e.prospect)}
        >
          {displayName(e.prospect)}
        </div>
      ),
    },
    {
      key: 'contractNumber',
      header: 'N° contrat',
      className: 'whitespace-nowrap',
      render: (e) => <span className="font-mono text-xs text-text-muted">{e.contractNumber}</span>,
    },
    {
      key: 'contractType',
      header: 'Type',
      className: 'whitespace-nowrap',
      render: (e) => (
        <span className="text-xs text-text-primary" title={e.contractType.label}>
          {e.contractType.code}
        </span>
      ),
    },
    {
      key: 'annualPremiumHt',
      header: 'Prime HT',
      align: 'right',
      className: 'whitespace-nowrap',
      render: (e) => euro(e.annualPremiumHt),
    },
    {
      key: 'annualCommission',
      header: 'Commission',
      align: 'right',
      className: 'whitespace-nowrap',
      render: (e) => euro(e.annualCommission),
    },
    {
      key: 'dueDate',
      header: 'Échéance',
      className: 'whitespace-nowrap',
      render: (e) => <DueDate iso={e.dueDate} />,
    },
    {
      key: 'reason',
      header: 'Raison',
      render: (e) => (
        <div className="max-w-[260px] truncate text-text-muted" title={e.reason}>
          {e.reason}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      className: 'whitespace-nowrap',
      render: (e) => (
        <button
          type="button"
          onClick={(ev) => {
            ev.stopPropagation();
            void handleDelete(e);
          }}
          aria-label="Supprimer"
          className="rounded-md p-1 text-text-muted transition-colors hover:bg-danger-50 hover:text-danger"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Client Jeopardy"
        className="mb-2"
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-white px-3 py-1.5 text-xs font-medium text-text-muted transition-colors hover:border-accent/40 hover:text-brand"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filtres
              {activeFilters.length > 0 && (
                <span className="ml-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-white">
                  {activeFilters.length}
                </span>
              )}
            </button>
            <Button
              variant="primary"
              leadingIcon={<Plus className="h-4 w-4" />}
              onClick={() => setShowCreate(true)}
            >
              Signaler un contrat
            </Button>
          </div>
        }
      />

      <p className="mb-4 text-sm text-text-muted">
        Contrats clients en vigueur identifiés comme à risque de perte, triés par échéance.
      </p>

      {/* Bascule de périmètre (Mon / Équipe), même design que le Pipeline. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex overflow-hidden rounded-md border border-border-subtle bg-white">
          <button
            type="button"
            onClick={() => setAssignedUserId(currentUserId)}
            aria-pressed={currentUserId !== '' && assignedUserId === currentUserId}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors',
              currentUserId !== '' && assignedUserId === currentUserId
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:text-brand',
            )}
          >
            <User className="h-3.5 w-3.5" />
            Mes clients
          </button>
          <button
            type="button"
            onClick={() => setAssignedUserId('')}
            aria-pressed={assignedUserId === ''}
            className={cn(
              'flex items-center gap-1.5 border-l border-border-subtle px-3 py-1.5 text-xs font-medium transition-colors',
              assignedUserId === ''
                ? 'bg-accent/10 text-accent'
                : 'text-text-muted hover:text-brand',
            )}
          >
            <Users className="h-3.5 w-3.5" />
            Équipe
          </button>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-text-muted">
            Filtres actifs
          </span>
          {activeFilters.map((f) => (
            <span
              key={f.key}
              className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent"
            >
              {f.label}
              <button
                type="button"
                onClick={() => clearFilter(f.key)}
                aria-label={`Retirer le filtre ${f.label}`}
                className="rounded-full p-0.5 hover:bg-accent/20"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <Banner tone="danger" className="mb-4">
          {error}
        </Banner>
      )}

      <DataTable
        columns={columns}
        data={items}
        keyExtractor={(e) => e.id}
        loading={loading && items.length === 0}
        onRowClick={(e) => setEditing(e)}
        empty={{
          title: 'Aucun contrat en danger',
          description:
            'Signalez un contrat client à risque avec le bouton « Signaler un contrat ».',
        }}
      />

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" onClick={() => void load(nextCursor)} disabled={loading}>
            {loading ? 'Chargement…' : 'Charger plus'}
          </Button>
        </div>
      )}

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Filtres"
        footer={
          <div className="flex items-center justify-between">
            <span className="text-xs text-text-muted">
              {activeFilters.length} filtre{activeFilters.length > 1 ? 's' : ''} actif
              {activeFilters.length > 1 ? 's' : ''}
            </span>
            <Button
              variant="secondary"
              onClick={clearAllFilters}
              disabled={activeFilters.length === 0}
            >
              Tout effacer
            </Button>
          </div>
        }
      >
        <div className="space-y-5">
          <div>
            <Label htmlFor="jeo-f-search">Client</Label>
            <Input
              id="jeo-f-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nom / raison sociale"
            />
          </div>
          <div>
            <Label htmlFor="jeo-f-number">Numéro de contrat</Label>
            <Input
              id="jeo-f-number"
              value={contractNumber}
              onChange={(e) => setContractNumber(e.target.value)}
              placeholder="Ex. POL-2024"
            />
          </div>
          <div>
            <Label htmlFor="jeo-f-type">Type de contrat</Label>
            <Select
              id="jeo-f-type"
              value={contractTypeId}
              onChange={(e) => setContractTypeId(e.target.value)}
            >
              <option value="">Tous</option>
              {contractTypes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} - {c.label}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Prime annuelle HT (€)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                min="0"
                value={minPremium}
                onChange={(e) => setMinPremium(e.target.value)}
                placeholder="Min"
                aria-label="Prime minimum"
              />
              <Input
                type="number"
                min="0"
                value={maxPremium}
                onChange={(e) => setMaxPremium(e.target.value)}
                placeholder="Max"
                aria-label="Prime maximum"
              />
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Commission annuelle (€)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                min="0"
                value={minCommission}
                onChange={(e) => setMinCommission(e.target.value)}
                placeholder="Min"
                aria-label="Commission minimum"
              />
              <Input
                type="number"
                min="0"
                value={maxCommission}
                onChange={(e) => setMaxCommission(e.target.value)}
                placeholder="Max"
                aria-label="Commission maximum"
              />
            </div>
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
              Échéance
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="jeo-f-due-after" className="text-xs font-normal text-text-muted">
                  Après le
                </Label>
                <Input
                  id="jeo-f-due-after"
                  type="date"
                  value={dueAfter}
                  onChange={(e) => setDueAfter(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="jeo-f-due-before" className="text-xs font-normal text-text-muted">
                  Avant le
                </Label>
                <Input
                  id="jeo-f-due-before"
                  type="date"
                  value={dueBefore}
                  onChange={(e) => setDueBefore(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </Drawer>

      {showCreate && (
        <CreateJeopardyModal
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            toast({
              tone: 'success',
              title: 'Contrat signalé',
              message: 'Le contrat a été ajouté à la liste.',
            });
            void load();
          }}
        />
      )}
      {editing && (
        <CreateJeopardyModal
          initialEntry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast({
              tone: 'success',
              title: 'Signalement mis à jour',
              message: 'Les modifications ont été enregistrées.',
            });
            void load();
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Cellule Échéance : code couleur rouge (échu) / orange (< 30 j) / neutre.
// La date est parsée localement depuis sa partie `YYYY-MM-DD` pour éviter tout
// décalage de fuseau (la valeur est stockée à minuit UTC).
// ---------------------------------------------------------------------------

function DueDate({ iso }: { iso: string }) {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  const due = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const label = due.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const overdue = days < 0;
  const soon = days >= 0 && days <= 30;
  const tone = overdue ? 'text-danger' : soon ? 'text-warning-600' : 'text-text-primary';
  const hint = overdue ? 'Échu' : soon ? `J-${days}` : '';

  return (
    <span className={cn('inline-flex items-center gap-1.5 font-medium', tone)}>
      {label}
      {hint && (
        <span
          className={cn(
            'rounded px-1 py-0.5 text-[10px] font-semibold',
            overdue ? 'bg-danger-50' : 'bg-warning-50',
          )}
        >
          {hint}
        </span>
      )}
    </span>
  );
}

function euro(value: string): string {
  return `${Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
}

function rangeLabel(min: string, max: string): string {
  if (min !== '' && max !== '') return `${min} → ${max}`;
  if (min !== '') return `≥ ${min}`;
  return `≤ ${max}`;
}

function dateRangeLabel(after: string, before: string): string {
  const fmt = (s: string) =>
    s
      ? new Date(`${s}T00:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
      : '?';
  if (after && before) return `${fmt(after)} → ${fmt(before)}`;
  if (after) return `après ${fmt(after)}`;
  return `avant ${fmt(before)}`;
}
